// ─── Fiche INFO d'une occurrence (lecture seule) ──────────────────
// Ouverte au TAP sur une ligne du mois (principe app-wide : le clic informe,
// il ne modifie pas). ✏️ en en-tête fait passer en édition ; les changements
// d'ÉTAT du mois (n'a pas eu lieu / ignorer / rétablir) sont dans le corps,
// car ils ne concernent que CE mois-ci, pas la ligne prévue elle-même.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, frJMA, useUI, useT } from '@mop/core';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { emojiCategorie, cleCategorie } from './data/categories';
import { STATUTS, estClos } from './data/statuts';


export default function FicheOccurrence({
  visible, occurrence, actuals = [], rattachements = [], nomBudget = '',
  onFermer, onEditer, onStatut, onRetablir, onDetacher, onRattacherOperation,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  if (!occurrence) return null;

  const o = occurrence;
  const st = STATUTS[o.statut] || STATUTS.avenir;
  // « Clos » = ferme PAR L'UTILISATEUR (cf. data/statuts.js).
  const clos = estClos(o);
  const revenu = o.ligne?.sens === 'revenu';
  // Ce qui justifie le « réel » affiché — donc on le montre. Avec le modèle
  // n↔n, ce n'est plus l'opération entière mais la PART qui lui est affectée :
  // afficher 1 000 € pour une part de 850 € ferait mentir le total juste
  // au-dessus. On garde l'opération à côté, pour la reconnaître.
  const rattaches = (rattachements || [])
    .filter(r => r && r.ligneId === o.ligneId && r.mois === o.mois)
    .map(r => ({ ...r, op: actuals.find(a => a.id === r.depenseId) || null }));
  const couleurEcart = o.ecart < -0.005 ? UI.muted : o.ecart > 0.005 ? (UI.danger || '#c0392b') : UI.ink;

  return (
    <Feuille visible={visible} titre={o.ligne?.libelle || ''} emoji={emojiCategorie(o.ligne?.categorie)}
      hauteur={0.8} onFermer={onFermer}
      action={{ icone: '✏️', label: t('finances.actModifierLigne'), onPress: onEditer }}>

      <Text style={s.sous}>
        {st.emoji} {t(st.cle)}{nomBudget ? `  ·  ${nomBudget}` : ''}  ·  {t(cleCategorie(o.ligne?.categorie))}
      </Text>

      <View style={s.totaux}>
        <View style={s.bloc}>
          <Text style={s.blocLbl}>{t('finances.suiviPrevu')}</Text>
          <Text style={[s.blocVal, revenu && { color: UI.ok || '#2e7d32' }]}>{formatEuro(o.prevu)}</Text>
        </View>
        <View style={s.bloc}>
          <Text style={s.blocLbl}>{t('finances.suiviReel')}</Text>
          <Text style={[s.blocVal, revenu && { color: UI.ok || '#2e7d32' }]}>{formatEuro(o.reel)}</Text>
        </View>
        <View style={s.bloc}>
          <Text style={s.blocLbl}>{t('finances.suiviEcart')}</Text>
          <Text style={[s.blocVal, { color: couleurEcart }]}>
            {o.ecart < 0 ? `${t('finances.reste')} ` : o.ecart > 0 ? `${t('finances.depasse')} ` : ''}
            {formatEuro(Math.abs(o.ecart))}
          </Text>
        </View>
      </View>

      {/* Opérations rattachées — swipe non disponible ici (fiche), donc un
          bouton de détachement explicite par ligne. */}
      <Text style={s.titre}>{t('finances.operationsRattachees')}</Text>
      {rattaches.length === 0 ? (
        <Text style={s.vide}>{t('finances.aucuneOpRattachee')}</Text>
      ) : rattaches.map(r => {
        const a = r.op || {};
        // Part < opération : on le dit. Sans cette mention, une opération de
        // 1 000 € affectée pour 850 € apparaîtrait comme une opération de
        // 850 €, et on la chercherait en vain sur son relevé bancaire.
        const partielle = Math.abs(Math.abs(a.montant || 0) - Math.abs(r.montant || 0)) > 0.005;
        return (
          <View key={r.id} style={s.op}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.opLib} numberOfLines={1}>{a.libelle || t(cleCategorie(a.categorie))}</Text>
              <Text style={s.opDate}>
                {frJMA(dateEffective(a))}
                {partielle ? `  ·  ${t('finances.partDe', { total: formatEuro(Math.abs(a.montant || 0)) })}` : ''}
              </Text>
            </View>
            <Text style={[s.opMontant, a.sens === 'revenu' && { color: UI.ok || '#2e7d32' }]}>
              {montantOperation(r.montant, a.sens)}
            </Text>
            <TouchableOpacity onPress={() => onDetacher?.(r)} hitSlop={HIT} accessibilityLabel={t('finances.detacher')}>
              <Text style={{ fontSize: 15, color: UI.muted }}>⊝</Text>
            </TouchableOpacity>
          </View>
        );
      })}

      {/* Rattacher une opération du compte à cette échéance — le sens que
          Cédric a demandé le 12/08 (« en cliquant sur une opération, on doit
          pouvoir la rattacher à une opération du compte courant »). */}
      {!!onRattacherOperation && (
        <TouchableOpacity style={s.rattacher} activeOpacity={0.7} onPress={onRattacherOperation}>
          <Text style={s.rattacherTxt}>🔗 {t('finances.actRattacher')}</Text>
        </TouchableOpacity>
      )}

      <Text style={s.titre}>{t('finances.etatDuMois')}</Text>
      <View style={s.actions}>
        <TouchableOpacity style={[s.btn, o.statut === 'absente' && s.btnOn]} activeOpacity={0.7}
          onPress={() => onStatut?.('absente')}>
          <Text style={[s.btnTxt, o.statut === 'absente' && s.btnTxtOn]}>⛔ {t('finances.actAbsente')}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.btn, o.statut === 'ignoree' && s.btnOn]} activeOpacity={0.7}
          onPress={() => onStatut?.('ignoree')}>
          <Text style={[s.btnTxt, o.statut === 'ignoree' && s.btnTxtOn]}>🔕 {t('finances.actIgnorer')}</Text>
        </TouchableOpacity>

        {/* CLORE LE MOIS — un seul état, un seul vocabulaire.
            Le POURQUOI (12/08) : « le delta restera tel quel et tout le
            budget ne sera pas consommé » (Cédric). Sans cet état, une
            échéance sous-consommée réclamait indéfiniment un complément qui
            ne viendrait jamais. L'écart reste intact — c'est une information
            de suivi, pas une dette.
            Le COMMENT (13/08) : le 🏁 « Totalement consommée » d'ici et le
            cadenas de la feuille de rattachement posaient tous deux `soldee`
            sous deux apparences — rien ne disait que c'était le même état.
            Même icône désormais, et elle BASCULE. */}
        <TouchableOpacity style={[s.btn, clos && s.btnOn]} activeOpacity={0.7}
          onPress={() => (clos ? onRetablir?.() : onStatut?.('soldee'))}>
          <Text style={[s.btnTxt, clos && s.btnTxtOn]}>
            {clos ? `✅ ${t('finances.rouvrirMois')}` : `☑️ ${t('finances.rienDePlusMois')}`}
          </Text>
        </TouchableOpacity>
        {!!o.exception?.statut && !clos && (
          <TouchableOpacity style={s.btn} activeOpacity={0.7} onPress={onRetablir}>
            <Text style={s.btnTxt}>↺ {t('finances.actRetablir')}</Text>
          </TouchableOpacity>
        )}
      </View>
    </Feuille>
  );
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const makeStyles = (UI) => StyleSheet.create({
  sous: { fontSize: 13, color: UI.muted, fontWeight: '600', marginBottom: 10 },
  totaux: { flexDirection: 'row', gap: 10, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  bloc: { flex: 1 },
  blocLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  blocVal: { fontSize: 14.5, fontWeight: '800', color: UI.ink, marginTop: 2 },
  rattacher: { alignItems: 'center', paddingVertical: 12, borderRadius: 11, borderWidth: 1, borderColor: UI.accent, marginTop: 14 },
  rattacherTxt: { fontSize: 14, fontWeight: '800', color: UI.accent },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 16, marginBottom: 6 },
  vide: { fontSize: 13, color: UI.faint, paddingVertical: 6 },
  op: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 11, marginBottom: 7 },
  opLib: { fontSize: 14, fontWeight: '600', color: UI.ink },
  opDate: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  opMontant: { fontSize: 14, fontWeight: '800', color: UI.ink },
  actions: { gap: 8 },
  btn: { paddingVertical: 11, paddingHorizontal: 13, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnOn: { borderColor: UI.accent, borderWidth: 1.5 },
  btnTxt: { fontSize: 14, color: UI.ink, fontWeight: '600' },
  btnTxtOn: { color: UI.accent, fontWeight: '800' },
});
